import React, { Fragment } from "react";

// Read-only render of an MCQ question's options inside a run transcript (ported from florence-client's
// case-builder/TranscriptMCQ.jsx). Static option chips with the patient's chosen option(s) highlighted —
// no selection state, no submit. The parent decides WHETHER to render it: only when the patient reply
// matched ≥1 option (see `matchAnswerToOptions`), otherwise the reply shows as plain text.

const normalizeToken = (text) =>
  String(text ?? "").toLowerCase().trim().replace(/^["']+|["']+$/g, "").trim();

// Match a recorded patient reply to MCQ option id(s) by exact normalized label OR id. The emulator
// "clicks" with option ids (comma-separated for multi-select), so this mirrors the runtime's exact-match
// bypass: comma-split for multi, exact equality only (no fuzzy/substring — a false highlight would
// misreport what the patient chose). Returns the matched option ids; [] = no match → render plain text.
export const matchAnswerToOptions = (options, selection, answerText) => {
  if (!Array.isArray(options) || options.length === 0) return [];
  const cleaned = String(answerText ?? "").trim();
  if (!cleaned) return [];
  const tokens = (selection === "multi" ? cleaned.split(",") : [cleaned]).map(normalizeToken).filter(Boolean);
  if (tokens.length === 0) return [];
  const matched = [];
  for (const opt of options) {
    const label = normalizeToken(opt.label);
    const id = normalizeToken(opt.id);
    if (tokens.some((tok) => tok === label || tok === id)) matched.push(opt.id);
  }
  return matched;
};

// Group options by their `group` field (absent → 0), preserving first-seen order. Ungrouped MCQs
// render as one group with no dividers.
const groupOptions = (options) => {
  const buckets = new Map();
  for (const opt of options || []) {
    const g = opt.group ?? 0;
    if (!buckets.has(g)) buckets.set(g, []);
    buckets.get(g).push(opt);
  }
  return Array.from(buckets.values());
};

export default function TranscriptMCQ({ options, selection, selectedIds = [] }) {
  if (!Array.isArray(options) || options.length === 0) return null;
  const selected = new Set(selectedIds);
  return (
    <div className="transcript-mcq" aria-readonly="true">
      <div className="mcq-instruction">{selection === "multi" ? "Select all that apply:" : "Select one:"}</div>
      {groupOptions(options).map((opts, gi) => (
        <Fragment key={gi}>
          {gi > 0 && <div className="mcq-group-divider" />}
          <div className="mcq-options-grid">
            {opts.map((o) => (
              <span key={o.id} className={`mcq-option-button${selected.has(o.id) ? " selected" : ""}`}>{o.label}</span>
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
}
